/** The salaried switch. Flipping it asks first, because it changes how the
 *  person is shown and paid everywhere. The confirm stays open if the save fails. */
import { useEffect, useRef, useState } from 'react';

import SalariedConfirm from './SalariedConfirm';

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-moss-500';

export default function SalariedSwitch({
  isSalaried,
  memberName,
  busy,
  error,
  onConfirm,
}: {
  isSalaried: boolean;
  memberName: string;
  busy: boolean;
  error: string | null;
  onConfirm: (next: boolean) => Promise<boolean>;
}) {
  const [asking, setAsking] = useState(false);
  const switchRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const wasAsking = useRef(false);

  useEffect(() => {
    if (asking) confirmRef.current?.focus();
    else if (wasAsking.current) switchRef.current?.focus();
    wasAsking.current = asking;
  }, [asking]);

  async function confirm() {
    if (await onConfirm(!isSalaried)) setAsking(false);
  }

  return (
    <div
      className="flex flex-wrap items-center gap-3 text-sm"
      onKeyDown={(e) => {
        if (asking && e.key === 'Escape') setAsking(false);
      }}
    >
      <button
        ref={switchRef}
        type="button"
        role="switch"
        aria-checked={isSalaried}
        aria-labelledby="salaried-switch-label"
        disabled={busy}
        onClick={() => setAsking(true)}
        className={`relative h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${FOCUS} ${
          isSalaried ? 'bg-moss-600' : 'bg-stone-300 dark:bg-stone-600'
        }`}
      >
        <span
          className={`absolute top-1 h-4 w-4 rounded-full bg-white dark:bg-stone-100 transition-all ${
            isSalaried ? 'left-6' : 'left-1'
          }`}
        />
      </button>
      <span id="salaried-switch-label" className="text-stone-700 dark:text-stone-300">
        Salaried
      </span>
      {asking && (
        <SalariedConfirm
          memberName={memberName}
          makeSalaried={!isSalaried}
          busy={busy}
          confirmRef={confirmRef}
          onConfirm={() => void confirm()}
          onCancel={() => setAsking(false)}
        />
      )}
      {error && (
        <p role="alert" className="w-full text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
